import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Play } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Testimonials = () => {
  const [videoTestimonials, setVideoTestimonials] = useState([]);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/video-testimonials`);
      setVideoTestimonials(res.data);
    } catch (err) {
      // Error handled silently in production
    }
  };

  const testimonials = [
    { 
      name: 'Vicky Kumar (Bangalore)', 
      text: 'Excellent service from Jainesh sir! He guided me clearly through every step of the process and provided outstanding support with the filing. He was extremely proactive, attentive, and thorough throughout. What truly stood out was his clear communication and detailed explanations, which made everything easy to understand. Highly professional, responsive, and reliable — I highly recommend his services and will recommend my friend circle for his service', 
      rating: 5, 
      type: 'image',
      image: 'https://customer-assets.emergentagent.com/job_kothari-services/artifacts/b8czadjw_WhatsApp%20Image%202026-03-16%20at%206.56.22%20PM%20%281%29.jpeg'
    },
    { 
      name: 'The Fashion Aesthetic (GST Filing)', 
      text: 'you can definitely count on them!', 
      rating: 5, 
      type: 'image',
      image: 'https://customer-assets.emergentagent.com/job_kothari-services/artifacts/emzss2kn_WhatsApp%20Image%202026-03-16%20at%206.56.23%20PM.jpeg'
    },
    { 
      name: 'Karan Singh Rathore (Portfolio Review)', 
      text: 'Good services', 
      rating: 5, 
      type: 'image',
      image: 'https://customer-assets.emergentagent.com/job_kothari-services/artifacts/6i0u1rrb_WhatsApp%20Image%202026-03-16%20at%206.56.23%20PM%20%281%29.jpeg'
    },
    { 
      name: 'Padma Joshi Samvedi', 
      text: 'I got my ITR filed by Jainesh Sir and had a great experience. He is very helpful, clears all doubts patiently, and makes the whole process easy and hassle-free. Definitely recommended.', 
      rating: 5, 
      type: 'image',
      image: 'https://customer-assets.emergentagent.com/job_kothari-services/artifacts/x5tlwclg_WhatsApp%20Image%202026-03-16%20at%206.56.23%20PM%20%282%29.jpeg'
    },
    { 
      name: 'Honey Jain', 
      text: 'I had a great experience working with him as my financial advisor. He explains everything clearly and provides practical, well-thought-out guidance based on my financial goals. He is patient, knowledgeable, and always available to clarify doubts. His advice has really helped me make better financial decisions.', 
      rating: 5, 
      type: 'image',
      image: 'https://customer-assets.emergentagent.com/job_kothari-services/artifacts/9rr8rs4w_honey%20jain%20topmate%20testimonial.jpg.jpeg'
    }
  ];

  return (
    <div className="bg-background py-24 md:py-32" data-testid="testimonials-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">Client Reviews</p>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight font-playfair mb-6" data-testid="testimonials-title">
            What Our Clients Say
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl mx-auto">
            Read testimonials from our satisfied clients who have trusted us with their tax and financial needs.
          </p>
        </div>

        {/* Video Testimonials */}
        {videoTestimonials.length > 0 && (
          <div className="mb-16" data-testid="video-testimonials-section">
            <h2 className="text-2xl font-semibold font-playfair text-center mb-8 flex items-center justify-center gap-2">
              <Play className="h-6 w-6 text-secondary" />
              Video Testimonials
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videoTestimonials.map((video, idx) => (
                <Card key={video.id || idx} className="overflow-hidden hover:shadow-xl transition-all duration-300" data-testid={`video-testimonial-${idx}`}>
                  <div className="aspect-video bg-black">
                    <video
                      controls
                      preload="metadata"
                      className="w-full h-full object-contain"
                      poster=""
                    >
                      <source src={video.video_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-semibold text-sm">{video.client_name}</p>
                    {video.description && <p className="text-xs text-muted-foreground mt-1">{video.description}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Text/Image Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="hover:shadow-xl transition-all duration-300 overflow-hidden" data-testid={`testimonial-${index}`}>
              {testimonial.type === 'image' && testimonial.image ? (
                <div className="aspect-square w-full">
                  <img 
                    src={testimonial.image} 
                    alt={`Testimonial from ${testimonial.name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 italic">"{testimonial.text}"</p>
                  <p className="font-semibold">- {testimonial.name}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center bg-muted/20 p-12 rounded-xl">
          <h3 className="text-2xl font-semibold font-playfair mb-4" data-testid="testimonials-cta-title">
            Ready to Experience Excellence?
          </h3>
          <p className="text-muted-foreground mb-6">
            Join our growing list of satisfied clients.
          </p>
          <a href="/contact">
            <button className="bg-secondary text-white px-6 py-3 rounded-md hover:bg-secondary/90 transition-colors" data-testid="testimonials-contact-button">
              Contact Us Today
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
